import { createEffect, createEvent, createStore } from 'effector';
import request from '@helpers/request';
import api from '@constants/endpoints';
import { AxiosResponse } from 'axios';
import { DEFAULT_PAGE_SIZE } from '@constants/pagination';
import IComment from '@interfaces/IComment';
import IPost from '@interfaces/IPost';
import mentionsOfficialAccountPost from './mentionsOfficialAccountPost';
import mentionsOfficialAccountComment from './mentionsOfficialAccountComment';

type UnreadState = {
  mentionsOfficialAccountPost: boolean;
  mentionsOfficialAccountComment: boolean;
};

const setUnread = createEvent<{ listName: keyof UnreadState; value: boolean }>('setUnread');

export const getUnread = createEffect({
  name: 'getUnread',
  async handler() {
    const pageSize = DEFAULT_PAGE_SIZE;

    return await Promise.allSettled([
      request(
        api.mentionsOfficialAccount.getPost,
        {
          data: {
            includeChecked: false,
            pagination: { type: 'SKIP_UNTIL_DATE', pageSize, lastDate: null },
          },
        },
        { catch: false },
      ),
      request(
        api.mentionsOfficialAccount.getComment,
        {
          data: {
            includeChecked: false,
            pagination: { type: 'SKIP_UNTIL_DATE', pageSize, lastDate: null },
          },
        },
        { catch: false },
      ),
    ]);
  },
});

function handleGetUnreadSpams(
  state: UnreadState,
  responses: Array<PromiseSettledResult<AxiosResponse>>,
) {
  const next: UnreadState = { ...state };

  responses.forEach((response) => {
    if (response.status !== 'fulfilled') return;

    const { value } = response;

    const posts = value.data?.body?.posts;
    const comments = value.data?.body?.comments;
    const postsPage: IPost[] = Array.isArray(posts) ? posts : posts?.page ?? [];
    const commentsPage: IComment[] = Array.isArray(comments) ? comments : comments?.page ?? [];

    const url = value.config.url || '';

    const postUrl = api.mentionsOfficialAccount.getPost.url;
    const commentUrl = api.mentionsOfficialAccount.getComment.url;

    if (postUrl && url.includes(postUrl)) {
      next.mentionsOfficialAccountPost = postsPage.some((post) => !post.author?.isOfficial);
    }

    if (commentUrl && url.includes(commentUrl)) {
      next.mentionsOfficialAccountComment = commentsPage.some(
        (comment) => !comment.author?.isOfficial,
      );
    }
  });

  return next;
}

mentionsOfficialAccountPost.watch((store) => {
  const { list, filters, preloaders } = store;
  if (!preloaders.getList && !filters.needChecked) {
    setUnread({ listName: 'mentionsOfficialAccountPost', value: list.length > 0 });
  }
});

mentionsOfficialAccountComment.watch((store) => {
  const { list, filters, preloaders } = store;
  if (!preloaders.getList && !filters.needChecked) {
    setUnread({ listName: 'mentionsOfficialAccountComment', value: list.length > 0 });
  }
});

const mentionsOfficialAccountUnread = createStore<UnreadState>({
  mentionsOfficialAccountPost: false,
  mentionsOfficialAccountComment: false,
})
  .on(getUnread.doneData, handleGetUnreadSpams)
  .on(setUnread, (state, { listName, value }) => ({ ...state, [listName]: value }));

export default mentionsOfficialAccountUnread;
